import React, { useRef } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Chip,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { useFormik } from "formik";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useCreateEmailTemplateMutation } from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useNavigate } from "react-router";

// ─── Placeholder definitions ─────────────────────────────────────────────────
const PLACEHOLDERS = [
  { label: "Customer Name",   tag: "[customer_name]",   desc: "Full name of the customer" },
  { label: "Quote Number",    tag: "[quote_number]",     desc: "Work order / quote number" },
  { label: "Invoice Number",  tag: "[invoice_number]",   desc: "Invoice reference number" },
  { label: "Grand Total",     tag: "[grand_total]",      desc: "Total invoice amount" },
  { label: "Total Due",       tag: "[total_due]",        desc: "Outstanding balance" },
  { label: "Payment Link",    tag: "[payment_link]",     desc: "Online payment URL" },
  { label: "Trip Date",       tag: "[trip_date]",        desc: "Scheduled trip date" },
  { label: "Company Name",    tag: "[company_name]",     desc: "Your company name" },
];

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

const Index = () => {
  const { showErrorMessage, showMessage } = useNotifier();
  const navigate = useNavigate();
  const quillRef = useRef<ReactQuill>(null);
  const [newEmailTemplate, { isLoading }] = useCreateEmailTemplateMutation();

  // ── Insert placeholder at cursor in Quill ──────────────────────────────────
  const insertPlaceholder = (tag: string) => {
    const editor = quillRef.current?.getEditor();
    if (editor) {
      const range = editor.getSelection(true);
      editor.insertText(range.index, tag, "user");
      editor.setSelection(range.index + tag.length, 0);
    } else {
      // fallback: append to message
      formik.setFieldValue("message", formik.values.message + tag);
    }
  };

  const formik = useFormik({
    initialValues: { templateName: "", subject: "", message: "" },
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (!values.templateName.trim()) errors.templateName = "Template name is required";
      if (!values.subject.trim()) errors.subject = "Subject is required";
      const text = values.message.replace(/<\/?[^>]+(>|$)/g, "").trim();
      if (!text) errors.message = "Message body is required";
      return errors;
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        const res = await newEmailTemplate({
          template_name: values.templateName,
          subject: values.subject,
          message: values.message,
        }).unwrap();
        if (!res.status) {
          showErrorMessage(res.message);
        } else {
          showMessage("Template created successfully");
          resetForm();
          navigate("/email/list");
        }
      } catch {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      fontSize: 13,
      bgcolor: "#fff",
      "&:hover fieldset": { borderColor: "#2f3891" },
      "&.Mui-focused fieldset": { borderColor: "#2f3891", borderWidth: 2 },
    },
  };

  const labelSx = { fontSize: 12, fontWeight: 600, color: "#475569", mb: 0.5, display: "block" };

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, maxWidth: 1200, mx: "auto" }}>
      {/* ── Page header ──────────────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <Box
          sx={{
            width: 40, height: 40, borderRadius: 2,
            background: "linear-gradient(135deg,#2f3891,#2f3891)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <EmailOutlinedIcon sx={{ color: "#fff", fontSize: 20 }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 18, color: "#0f172a", lineHeight: 1 }}>
            Create Email Template
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.3 }}>
            Design a reusable template for client communications
          </Typography>
        </Box>
      </Box>

      {/* ── Two-column layout ─────────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start", flexWrap: { xs: "wrap", md: "nowrap" } }}>

        {/* ── Left: Placeholder panel ──────────────────────────────────────── */}
        <Box
          sx={{
            width: { xs: "100%", md: 260 },
            flexShrink: 0,
            bgcolor: "#fff",
            borderRadius: 3,
            border: "1px solid #e2e8f0",
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: 2, py: 1.5,
              background: "linear-gradient(135deg,#2f3891,#2f3891)",
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#fff" }}>
              Smart Placeholders
            </Typography>
            <Typography sx={{ fontSize: 11, color: "rgba(255,255,255,0.75)", mt: 0.3 }}>
              Click to insert at cursor
            </Typography>
          </Box>

          <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
            {PLACEHOLDERS.map((ph) => (
              <Tooltip key={ph.tag} title={ph.desc} placement="right" arrow>
                <Box
                  onClick={() => insertPlaceholder(ph.tag)}
                  sx={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    px: 1.5, py: 1, borderRadius: 2,
                    border: "1px solid #e2e8f0", cursor: "pointer",
                    transition: "all 0.15s",
                    "&:hover": { bgcolor: "#f0f0ff", borderColor: "#2f3891" },
                  }}
                >
                  <Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#1e293b" }}>
                      {ph.label}
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: "#2f3891", fontFamily: "monospace" }}>
                      {ph.tag}
                    </Typography>
                  </Box>
                  <ContentCopyIcon sx={{ fontSize: 13, color: "#94a3b8" }} />
                </Box>
              </Tooltip>
            ))}
          </Box>
        </Box>

        {/* ── Right: Form ──────────────────────────────────────────────────── */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              bgcolor: "#fff", borderRadius: 3,
              border: "1px solid #e2e8f0",
              boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
              overflow: "hidden",
            }}
          >
            {/* Card header */}
            <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #f1f5f9" }}>
              <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>
                Template Details
              </Typography>
            </Box>

            <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3 }}>
              {/* Template Name */}
              <Box>
                <Typography sx={labelSx}>Template Name *</Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="e.g. Quote Confirmation"
                  {...formik.getFieldProps("templateName")}
                  error={formik.touched.templateName && Boolean(formik.errors.templateName)}
                  helperText={formik.touched.templateName && formik.errors.templateName}
                  sx={fieldSx}
                />
              </Box>

              {/* Subject */}
              <Box>
                <Typography sx={labelSx}>Subject Line *</Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="e.g. Your Quote from TJ Bus – [quote_number]"
                  {...formik.getFieldProps("subject")}
                  error={formik.touched.subject && Boolean(formik.errors.subject)}
                  helperText={formik.touched.subject && formik.errors.subject}
                  sx={fieldSx}
                />
                <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.5 }}>
                  Tip: you can use placeholders like [customer_name] in the subject too
                </Typography>
              </Box>

              {/* Message body */}
              <Box>
                <Typography sx={labelSx}>Message Body *</Typography>
                <Box
                  sx={{
                    border: formik.touched.message && formik.errors.message
                      ? "1px solid #ef4444"
                      : "1px solid #e2e8f0",
                    borderRadius: 2,
                    overflow: "hidden",
                    "& .ql-toolbar": { borderBottom: "1px solid #e2e8f0", borderTop: 0, borderLeft: 0, borderRight: 0 },
                    "& .ql-container": { border: 0, fontSize: 13 },
                    "& .ql-editor": { minHeight: 260 },
                  }}
                >
                  <ReactQuill
                    ref={quillRef}
                    value={formik.values.message}
                    onChange={(val) => formik.setFieldValue("message", val)}
                    modules={QUILL_MODULES}
                    onBlur={() => formik.setFieldTouched("message", true)}
                  />
                </Box>
                {formik.touched.message && formik.errors.message && (
                  <Typography sx={{ fontSize: 11, color: "#ef4444", mt: 0.5 }}>
                    {formik.errors.message}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>

          {/* ── Action bar ─────────────────────────────────────────────────── */}
          <Box
            sx={{
              display: "flex", gap: 1.5, justifyContent: "flex-end",
              mt: 2, pt: 2, borderTop: "1px solid #f1f5f9",
            }}
          >
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/email/list")}
              sx={{
                borderRadius: 2, textTransform: "none", fontWeight: 600, fontSize: 13,
                borderColor: "#e2e8f0", color: "#64748b",
                "&:hover": { borderColor: "#94a3b8", bgcolor: "#f8fafc" },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={isLoading ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <SaveAltIcon />}
              onClick={() => formik.handleSubmit()}
              disabled={isLoading}
              sx={{
                borderRadius: 2, textTransform: "none", fontWeight: 600, fontSize: 13,
                background: "linear-gradient(135deg,#2f3891,#2f3891)",
                boxShadow: "0 2px 8px rgba(99,102,241,0.35)",
                px: 3,
                "&:hover": { background: "linear-gradient(135deg,#2f3891,#2f3891)" },
                "&:disabled": { background: "#e2e8f0", color: "#94a3b8", boxShadow: "none" },
              }}
            >
              Save Template
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Index;
